import {DispatchStatus} from "./dispatch-status";
import {LocationContext} from "./location/location-context";
import {RouteGeometry} from "./routing/route-geometry";
import {DatabaseCode, getDatabaseCode, toDatabaseCode} from "./database-code";
import * as t from "io-ts";
import {LocationContextCodec} from "./location/location-context";
import {RouteGeometryCodec} from "./routing/route-geometry";

export type TrackingConfidenceLevel = "HIGH" | "MEDIUM" | "LOW" | "NONE" | "UNKNOWN";
export type TrackingDestinationType = "PICKUP" | "DELIVERY";

export type DispatchTrackingConfidenceReasonCode =
  "NO_CURRENT_PERFORMER_LOCATION" |
  "MANUAL_DESTINATION_MATCHES_DISPATCH" |
  "PICKUP_TRANSPORT_EXISTS" |
  "STARTED_NO_STRONGER_COMPETING_SIGNAL" |
  "STARTED_WITH_COMPETING_DISPATCHES" |
  "ASSIGNED_ELSEWHERE_IN_DISPATCH_ORDER" |
  "PENDING_NOT_IN_POSSIBLE_DESTINATIONS" |
  "PENDING_NEXT_IN_DISPATCH_ORDER" |
  "LOCATION_CONTEXT_DESTINATION_NOT_STARTED" |
  "MULTIPLE_PENDING_DISPATCHES_SAME_ROUTE" |
  "ASSIGNED_NOT_STARTED_OR_SELECTED";

export type DispatchTrackingConfidenceSignalCode =
  "manual_destination_matches_dispatch" |
  "pickup_transport_exists" |
  "dispatch_started" |
  "next_dispatch_order_match" |
  "location_context_dispatch_destination_matches" |
  "location_context_assignment_destination_matches" |
  "perform_order_present" |
  "performer_has_competing_dispatches_with_same_route" |
  "another_dispatch_is_next_in_dispatch_order" |
  "dispatch_not_in_location_context_possible_destinations";

export interface DispatchTrackingConfidenceLevel extends DatabaseCode {
  code: TrackingConfidenceLevel;
}

export interface DispatchTrackingConfidenceReason extends DatabaseCode {
  code: DispatchTrackingConfidenceReasonCode;
}

export interface DispatchTrackingConfidenceSignal extends DatabaseCode {
  code: DispatchTrackingConfidenceSignalCode;
}

const high: DispatchTrackingConfidenceLevel = {
  code: "HIGH",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.level.high",
    defaultMessage: "High"
  }
};

const medium: DispatchTrackingConfidenceLevel = {
  code: "MEDIUM",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.level.medium",
    defaultMessage: "Medium"
  }
};

const low: DispatchTrackingConfidenceLevel = {
  code: "LOW",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.level.low",
    defaultMessage: "Low"
  }
};

const none: DispatchTrackingConfidenceLevel = {
  code: "NONE",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.level.none",
    defaultMessage: "None"
  }
};

const unknown: DispatchTrackingConfidenceLevel = {
  code: "UNKNOWN",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.level.unknown",
    defaultMessage: "Unknown"
  }
};

export const dispatchTrackingConfidenceLevel = {
  high,
  medium,
  low,
  none,
  unknown
};

export const dispatchTrackingConfidenceLevels = Object.values(dispatchTrackingConfidenceLevel);

export const toDispatchTrackingConfidenceLevel = (code: string): DispatchTrackingConfidenceLevel =>
  toDatabaseCode(code, dispatchTrackingConfidenceLevels);

export const getDispatchTrackingConfidenceLevel = (code: string | undefined): DispatchTrackingConfidenceLevel | undefined =>
  getDatabaseCode(code, dispatchTrackingConfidenceLevels);

const noCurrentPerformerLocation: DispatchTrackingConfidenceReason = {
  code: "NO_CURRENT_PERFORMER_LOCATION",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.reason.noCurrentPerformerLocation",
    defaultMessage: "No current performer location is available."
  }
};

const manualDestinationMatchesDispatch: DispatchTrackingConfidenceReason = {
  code: "MANUAL_DESTINATION_MATCHES_DISPATCH",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.reason.manualDestinationMatchesDispatch",
    defaultMessage: "The performer manually selected a next stop that matches this dispatch."
  }
};

const pickupTransportExists: DispatchTrackingConfidenceReason = {
  code: "PICKUP_TRANSPORT_EXISTS",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.reason.pickupTransportExists",
    defaultMessage: "The dispatch has a pickup transport, so the pickup has been performed."
  }
};

const startedNoStrongerCompetingSignal: DispatchTrackingConfidenceReason = {
  code: "STARTED_NO_STRONGER_COMPETING_SIGNAL",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.reason.startedNoStrongerCompetingSignal",
    defaultMessage: "The dispatch is started and no stronger competing dispatch signal was found."
  }
};

const startedWithCompetingDispatches: DispatchTrackingConfidenceReason = {
  code: "STARTED_WITH_COMPETING_DISPATCHES",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.reason.startedWithCompetingDispatches",
    defaultMessage: "The dispatch is started, but the performer has competing dispatches with similar routing."
  }
};

const assignedElsewhereInDispatchOrder: DispatchTrackingConfidenceReason = {
  code: "ASSIGNED_ELSEWHERE_IN_DISPATCH_ORDER",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.reason.assignedElsewhereInDispatchOrder",
    defaultMessage: "The performer appears to be assigned elsewhere in the current dispatch order, so this pending dispatch is not trackable with confidence."
  }
};

const pendingNotInPossibleDestinations: DispatchTrackingConfidenceReason = {
  code: "PENDING_NOT_IN_POSSIBLE_DESTINATIONS",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.reason.pendingNotInPossibleDestinations",
    defaultMessage: "This dispatch is pending and is not present in the performer's current location-context possible destinations."
  }
};

const pendingNextInDispatchOrder: DispatchTrackingConfidenceReason = {
  code: "PENDING_NEXT_IN_DISPATCH_ORDER",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.reason.pendingNextInDispatchOrder",
    defaultMessage: "This pending dispatch is next in the performer's dispatch order."
  }
};

const locationContextDestinationNotStarted: DispatchTrackingConfidenceReason = {
  code: "LOCATION_CONTEXT_DESTINATION_NOT_STARTED",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.reason.locationContextDestinationNotStarted",
    defaultMessage: "Location context points at a destination for this pending dispatch, but the dispatch has not been started."
  }
};

const multiplePendingDispatchesSameRoute: DispatchTrackingConfidenceReason = {
  code: "MULTIPLE_PENDING_DISPATCHES_SAME_ROUTE",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.reason.multiplePendingDispatchesSameRoute",
    defaultMessage: "The performer has multiple pending dispatches with the same pickup/delivery route, so this location may apply to another dispatch."
  }
};

const assignedNotStartedOrSelected: DispatchTrackingConfidenceReason = {
  code: "ASSIGNED_NOT_STARTED_OR_SELECTED",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.reason.assignedNotStartedOrSelected",
    defaultMessage: "The performer is assigned, but the dispatch has not been explicitly started or selected."
  }
};

export const dispatchTrackingConfidenceReason = {
  noCurrentPerformerLocation,
  manualDestinationMatchesDispatch,
  pickupTransportExists,
  startedNoStrongerCompetingSignal,
  startedWithCompetingDispatches,
  assignedElsewhereInDispatchOrder,
  pendingNotInPossibleDestinations,
  pendingNextInDispatchOrder,
  locationContextDestinationNotStarted,
  multiplePendingDispatchesSameRoute,
  assignedNotStartedOrSelected
};

export const dispatchTrackingConfidenceReasons = Object.values(dispatchTrackingConfidenceReason);

const dispatchTrackingConfidenceReasonByMessage: Record<string, DispatchTrackingConfidenceReason> = {
  "No current performer location is available.": dispatchTrackingConfidenceReason.noCurrentPerformerLocation,
  "The performer manually selected a next stop that matches this dispatch.": dispatchTrackingConfidenceReason.manualDestinationMatchesDispatch,
  "The dispatch has a pickup transport, so the pickup has been performed.": dispatchTrackingConfidenceReason.pickupTransportExists,
  "The dispatch is started and no stronger competing dispatch signal was found.": dispatchTrackingConfidenceReason.startedNoStrongerCompetingSignal,
  "The dispatch is started, but the performer has competing dispatches with similar routing.": dispatchTrackingConfidenceReason.startedWithCompetingDispatches,
  "The performer appears to be assigned elsewhere in the current dispatch order, so this pending dispatch is not trackable with confidence.": dispatchTrackingConfidenceReason.assignedElsewhereInDispatchOrder,
  "This dispatch is pending and is not present in the performer's current location-context possible destinations.": dispatchTrackingConfidenceReason.pendingNotInPossibleDestinations,
  "This pending dispatch is next in the performer's dispatch order.": dispatchTrackingConfidenceReason.pendingNextInDispatchOrder,
  "Location context points at a destination for this pending dispatch, but the dispatch has not been started.": dispatchTrackingConfidenceReason.locationContextDestinationNotStarted,
  "The performer has multiple pending dispatches with the same pickup/delivery route, so this location may apply to another dispatch.": dispatchTrackingConfidenceReason.multiplePendingDispatchesSameRoute,
  "The performer is assigned, but the dispatch has not been explicitly started or selected.": dispatchTrackingConfidenceReason.assignedNotStartedOrSelected
};

export const toDispatchTrackingConfidenceReason = (codeOrMessage: string): DispatchTrackingConfidenceReason => {
  return dispatchTrackingConfidenceReasonByMessage[codeOrMessage] ?? toDatabaseCode(codeOrMessage, dispatchTrackingConfidenceReasons);
}

export const getDispatchTrackingConfidenceReason = (code: string | undefined): DispatchTrackingConfidenceReason | undefined =>
  getDatabaseCode(code, dispatchTrackingConfidenceReasons);

const manualDestinationMatchesDispatchSignal: DispatchTrackingConfidenceSignal = {
  code: "manual_destination_matches_dispatch",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.signal.manualDestinationMatchesDispatch",
    defaultMessage: "Manual destination matches dispatch"
  }
};

const pickupTransportExistsSignal: DispatchTrackingConfidenceSignal = {
  code: "pickup_transport_exists",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.signal.pickupTransportExists",
    defaultMessage: "Pickup transport exists"
  }
};

const dispatchStartedSignal: DispatchTrackingConfidenceSignal = {
  code: "dispatch_started",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.signal.dispatchStarted",
    defaultMessage: "Dispatch started"
  }
};

const nextDispatchOrderMatchSignal: DispatchTrackingConfidenceSignal = {
  code: "next_dispatch_order_match",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.signal.nextDispatchOrderMatch",
    defaultMessage: "Next dispatch order match"
  }
};

const locationContextDispatchDestinationMatchesSignal: DispatchTrackingConfidenceSignal = {
  code: "location_context_dispatch_destination_matches",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.signal.locationContextDispatchDestinationMatches",
    defaultMessage: "Location context dispatch destination matches"
  }
};

const locationContextAssignmentDestinationMatchesSignal: DispatchTrackingConfidenceSignal = {
  code: "location_context_assignment_destination_matches",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.signal.locationContextAssignmentDestinationMatches",
    defaultMessage: "Location context assignment destination matches"
  }
};

const performOrderPresentSignal: DispatchTrackingConfidenceSignal = {
  code: "perform_order_present",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.signal.performOrderPresent",
    defaultMessage: "Perform order present"
  }
};

const performerHasCompetingDispatchesWithSameRouteSignal: DispatchTrackingConfidenceSignal = {
  code: "performer_has_competing_dispatches_with_same_route",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.signal.performerHasCompetingDispatchesWithSameRoute",
    defaultMessage: "Performer has competing dispatches with the same route"
  }
};

const anotherDispatchIsNextInDispatchOrderSignal: DispatchTrackingConfidenceSignal = {
  code: "another_dispatch_is_next_in_dispatch_order",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.signal.anotherDispatchIsNextInDispatchOrder",
    defaultMessage: "Another dispatch is next in dispatch order"
  }
};

const dispatchNotInLocationContextPossibleDestinationsSignal: DispatchTrackingConfidenceSignal = {
  code: "dispatch_not_in_location_context_possible_destinations",
  displayValue: {
    id: "common.entity.dispatch.tracking.confidence.signal.dispatchNotInLocationContextPossibleDestinations",
    defaultMessage: "Dispatch is not in location-context possible destinations"
  }
};

export const dispatchTrackingConfidenceSignal = {
  manualDestinationMatchesDispatch: manualDestinationMatchesDispatchSignal,
  pickupTransportExists: pickupTransportExistsSignal,
  dispatchStarted: dispatchStartedSignal,
  nextDispatchOrderMatch: nextDispatchOrderMatchSignal,
  locationContextDispatchDestinationMatches: locationContextDispatchDestinationMatchesSignal,
  locationContextAssignmentDestinationMatches: locationContextAssignmentDestinationMatchesSignal,
  performOrderPresent: performOrderPresentSignal,
  performerHasCompetingDispatchesWithSameRoute: performerHasCompetingDispatchesWithSameRouteSignal,
  anotherDispatchIsNextInDispatchOrder: anotherDispatchIsNextInDispatchOrderSignal,
  dispatchNotInLocationContextPossibleDestinations: dispatchNotInLocationContextPossibleDestinationsSignal
};

export const dispatchTrackingConfidenceSignals = Object.values(dispatchTrackingConfidenceSignal);

export const toDispatchTrackingConfidenceSignal = (code: string): DispatchTrackingConfidenceSignal =>
  toDatabaseCode(code, dispatchTrackingConfidenceSignals);

export const getDispatchTrackingConfidenceSignal = (code: string | undefined): DispatchTrackingConfidenceSignal | undefined =>
  getDatabaseCode(code, dispatchTrackingConfidenceSignals);

export const DispatchTrackingPerformerValidator = t.partial({
  personId: t.string,
  personName: t.string,
  personCompanyId: t.string,
  personCompanyName: t.string,
  tenantCode: t.string,
  assignmentStarted: t.boolean,
  assignmentDttm: t.number,
  locationContext: LocationContextCodec,
  locationDttm: t.number,
});

export const DispatchTrackingPerformerArrayValidator = t.array(DispatchTrackingPerformerValidator);

export const DispatchTrackingDestinationValidator = t.partial({
  type: t.keyof({
    PICKUP: null,
    DELIVERY: null,
  }),
  siteId: t.string,
  siteName: t.string,
  siteCompanyId: t.string,
  siteCompanyName: t.string,
  latitude: t.number,
  longitude: t.number,
  status: t.string,
});

export const DispatchTrackingRouteValidator = t.partial({
  status: t.string,
  distanceMiles: t.number,
  durationMillis: t.number,
  etaDttm: t.number,
  geometry: RouteGeometryCodec,
  message: t.string,
});

export const DispatchTrackingConfidenceValidator = t.partial({
  level: t.keyof({
    HIGH: null,
    MEDIUM: null,
    LOW: null,
    NONE: null,
    UNKNOWN: null,
  }),
  trackable: t.boolean,
  reason: t.string,
  signals: t.array(t.string),
  warnings: t.array(t.string),
  competingDispatchCount: t.number,
  competingDispatchIds: t.array(t.string),
  nextDispatchId: t.string,
});

export const DispatchTrackingResultValidator = t.intersection([
  t.type({
    dispatchId: t.string,
  }),
  t.partial({
    status: t.string,
    performer: DispatchTrackingPerformerValidator,
    destination: DispatchTrackingDestinationValidator,
    route: DispatchTrackingRouteValidator,
    confidence: DispatchTrackingConfidenceValidator,
    message: t.string,
  }),
]);

export const DispatchRequestTrackingResultValidator = t.intersection([
  t.type({
    dispatchRequestId: t.string,
  }),
  t.partial({
    status: t.string,
    performer: DispatchTrackingPerformerValidator,
    performers: DispatchTrackingPerformerArrayValidator,
    destination: DispatchTrackingDestinationValidator,
    route: DispatchTrackingRouteValidator,
    confidence: DispatchTrackingConfidenceValidator,
    message: t.string,
  }),
]);

export type DispatchTrackingPerformer = t.TypeOf<typeof DispatchTrackingPerformerValidator> & {
  locationContext?: LocationContext;
};
export type DispatchTrackingDestination = t.TypeOf<typeof DispatchTrackingDestinationValidator>;
export type DispatchTrackingRoute = t.TypeOf<typeof DispatchTrackingRouteValidator> & {
  geometry?: RouteGeometry;
};
export type DispatchTrackingConfidence = t.TypeOf<typeof DispatchTrackingConfidenceValidator>;
export type DispatchTrackingResult = t.TypeOf<typeof DispatchTrackingResultValidator> & {
  status?: DispatchStatus | string;
};
export type DispatchRequestTrackingResult = t.TypeOf<typeof DispatchRequestTrackingResultValidator>;
